import { Circ, Expo, Linear, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { Button, OutlinedButton } from "../gameobjects/Button";
import {AbstractGameState} from "./AbstractGameState";

import { Application } from "../Application";
import {TrashCollectIntroLayout} from "../layout/TrashCollectIntroLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { LightsOutElmoBathroomScreen } from "./LightsOutElmoBathroomScreen";
import { LightsOutElmoBedroomScreen } from "./LightsOutElmoBedroomScreen";
import { LightsOutElmoKitchenScreen } from "./LightsOutElmoKitchenScreen";
import { LightsOutElmoLivingRoomScreen } from "./LightsOutElmoLivingRoomScreen";
import { LightsOutIntroScreen } from "./LightsOutIntroScreen";
import { PlanterCustomizeIntroScreen } from "./PlanterCustomizeIntroScreen";
import { TrashCollectIntroScreen } from "./TrashCollectIntroScreen";

import { Grover } from "../gameobjects/Grover";
import {IntroLayout} from "../layout/IntroLayout";

import * as VO from "../data/VO";

const introVOS = [...VO.IA01, 0.5, VO.IA02];

export class Intro extends AbstractGameState {

    public static readonly NAME = "Intro";

    public readonly bg = this.addChild(new Sprite());
    public readonly bee = this.addChild(new Button());
    public readonly grover = this.addChild(new Grover());
    public readonly skipButton = this.addChild(new OutlinedButton());
    protected analyticsScreenName: string = "2 Intro Screen";
    protected analyticsRoundName = undefined;
    protected hoverTimelines: TimelineMax[] = [];

    public init(pSize: PIXI.Point, pData: {}) {
        IntroLayout.call(this);

        this.bee.interactive = false;
        this.bee.buttonMode = false;
        this.makeHover(this.bee);

        const startTime = new Date().valueOf();
        this.skipButton.onClick.add(() => {
            const now = new Date().valueOf();
            const duration = (now - startTime) / 1000;
            this.trackEvent("gameSkip", {label: `ScreenName_${this.analyticsScreenName}`, value: duration});

            this.gotoNextState("MainMenu");
        });

        this.skipButton.clickSfx = "buttonclick";
        super.init(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("main_menu_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("main_menu_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        const destinationX = this.grover.x;
        this.grover.x = 0;
        this.grover.walkTo(destinationX, 150, Ease.none, () => {
            HLF.Application.instance.voiceover.playVO(introVOS, () => {
                this.skipButton.visible = false;
                this.gotoNextState("MainMenu");
            });
        }, 1);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.grover.update(pDeltaTime);
    }

    public destroy() {
        this.hoverTimelines.forEach((t) => {
            t.kill();
            t.clear();
        });
        super.destroy();
    }

    private makeHover(pTarget: Button) {
        const s = 50; // radius of figure 8
        const d = 1; // speed in seconds to do a quarter of the animation

        const hoverTimeline = new TimelineMax();
        hoverTimeline.to(pTarget.sprite.position, d, {
            bezier: {
                type: "thru",
                values: [
                    { x: s / 2, y: s / 2 },
                    { x: s, y: 0 },
                ],
            },
            ease: Linear.easeNone,
        })
            .call(() => {
                pTarget.sprite.scale.x *= -1;
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: s / 2, y: -s / 2 },
                        { x: 0, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: -s / 2, y: s / 2 },
                        { x: -s, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            })
            .call(() => {
                pTarget.sprite.scale.x *= -1;
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: -s / 2, y: -s / 2 },
                        { x: 0, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            });

        hoverTimeline.repeat(-1);

        this.hoverTimelines.push(hoverTimeline);
    }
}
